import * as Rx from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';

import type { PaginationProps, PaginationResponse } from 'v2/interfaces/pagination';
import type { ShoppingDeliveriesResponse, ShoppingDelivery } from 'v2/interfaces/shopping';

import apiService from '../api';
import tableService from '../table';

import type { ApiService } from '../api';
import type { TableService } from '../table';

export class ShoppingDeliveriesService {
  private paginationParamsDefault: PaginationProps = { page: 1, itemsPerPage: 50 };
  private deliveriesList$ = new Rx.BehaviorSubject<ShoppingDelivery[]>([]);
  private paginationParams$ = new Rx.BehaviorSubject<PaginationProps>(this.paginationParamsDefault);
  private paginationResponse$ = new Rx.BehaviorSubject<PaginationResponse>({ ...this.paginationParamsDefault, pages: 0, totalItems: 0 });
  private openQuickview$ = new Rx.BehaviorSubject<boolean>(false);
  private current$ = new Rx.BehaviorSubject<ShoppingDelivery>(null);

  constructor(private apiService: ApiService, private tableService: TableService) {}

  public getPaginationParams = (): Rx.Observable<PaginationProps> => this.paginationParams$;

  public getPaginationResponse = (): Rx.Observable<PaginationResponse> => this.paginationResponse$;

  public setPagination(pagination: PaginationProps) {
    this.paginationParams$.next(pagination);
  }
  public setCurrent = (subscription: ShoppingDelivery): void => this.current$.next(subscription);
  public getCurrent = (): Rx.Observable<ShoppingDelivery> => this.current$;

  public getPagination = (): Rx.Observable<PaginationProps> => this.paginationParams$;
  public getQuickview = (): Rx.Observable<boolean> => this.openQuickview$;

  public openQuickview = (delivery: ShoppingDelivery): void => {
    this.setCurrent(delivery);
    this.openQuickview$.next(true);
  };

  public closeQuickview = (): void => {
    this.setCurrent(null);
    this.openQuickview$.next(false);
  };

  public list(): Rx.Observable<ShoppingDelivery[]> {
    return this.deliveriesList$;
  }

  public loadDeliveries() {
    return this.getPagination().pipe(
      tap(() => this.tableService.changeLoading(true)),
      debounceTime(10),
      switchMap(
        (pagination): Rx.Observable<ShoppingDeliveriesResponse> => this.apiService.get('/customers-api/customers/orders/deliveries', pagination)
      ),
      tap((response) => this.deliveriesList$.next(response.data.items)),
      tap((response) =>
        this.paginationResponse$.next({
          itemsPerPage: response.data.itemsPerPage,
          pages: response.data.pages,
          page: response.data.page,
          totalItems: response.data.totalItems,
        })
      ),
      tap(() => this.tableService.changeLoading(false)),
      catchError(() => {
        this.tableService.changeLoading(false);
        this.deliveriesList$.next([]);
        this.paginationResponse$.next(null);
        return Rx.of([]);
      })
    );
  }

  public loadFiscalDocuments = (invoiceId: string) => {
    return this.apiService.get(`/shopping/${invoiceId}/fiscaldocuments`);
  };

  public producerContact = (invoiceId: string) => {
    return this.apiService.get(`/shopping/${invoiceId}/contactproducer`);
  };
}
const shoppingProductService = new ShoppingDeliveriesService(apiService, tableService);
export default shoppingProductService;
